import React from 'react';
import {useTranslation} from 'react-i18next';
import Grid from '@mui/material/Grid';
import Panel from 'components/Panel';
import Button from 'components/Button';
import {filterByObjectType} from 'common/utils';
import {DiplomatObjectType} from 'types/DiplomatObjectType';
import SearchMoveForm from 'components/forms/SearchMoveForm';
import SearchResults from 'components/SearchResults';
import SearchResultsList from 'components/SearchResultsList';
import Box from '@mui/material/Box';
import {useDispatch} from 'react-redux';
import {IDiplomatObject} from 'types/DiplomatObject';
import {useGetApi} from 'common/hooks/useGetApi';
import {usePostApi} from 'common/hooks/usePostApi';
import {layoutActions} from 'features/layout/redux';
import {DialogTypes} from 'common/names';
import api from 'api';
import {ISearchCriteria, SearchMethod} from 'components/forms/SearchSchema';

type SearchTransactionFormProps = {
   onClose?: () => void;
};

const SearchTransactionForm = ({onClose}: SearchTransactionFormProps) => {
   const {t} = useTranslation();
   const dispatch = useDispatch();
   const [results, setResults] = React.useState<IDiplomatObject[]>([] as IDiplomatObject[]);

   const opts = React.useMemo(
      () => ({
         onSuccess: (objs: IDiplomatObject[]) => {
            setResults((res) => [...res, ...objs]);
         },
      }),
      [setResults]
   );

   const {request: searchNoJobHistoryTransactions} = useGetApi(api.noJobHistoryTransactions, opts);
   const {request: searchNoJobHistorySync} = useGetApi(api.noJobHistorySynchronizations, opts);

   const searchNoJobHistory = () => {
      setResults([]);
      searchNoJobHistorySync();
      searchNoJobHistoryTransactions();
   };

   const {request: searchUnusedTransactions} = useGetApi(api.unusedTransactions, {
      onSuccess: setResults,
   });
   const {request: searchTransactions} = useGetApi(api.searchTransactions, {
      onSuccess: setResults,
   });
   const {request: exportTransactions} = usePostApi(api.exportTransactions, {
      onSuccess: () => {
         dispatch(
            layoutActions.openSubDialog({
               id: DialogTypes.AlertDialog.id,
               title: t('Transaction Export'),
               content: t('Selected transactions successfully exported'),
            })
         );
      },
   });

   const handleExport = React.useCallback(
      (csvFile: string) => {
         exportTransactions(
            JSON.stringify({
               csvFile,
               transactions: results,
            })
         );
      },
      [exportTransactions, results]
   );

   const handleSubmit = (values: ISearchCriteria) => {
      if (values.method === SearchMethod.field) {
         searchTransactions(
            `?searchType=${values.searchField}&searchText=${values.containingText}`
         );
      }
      if (values.method === SearchMethod.noHistory) {
         searchNoJobHistory();
      }
      if (values.method === SearchMethod.noProcessedFiles) {
         searchUnusedTransactions(`?days=${values.daysUnused}`);
      }
      return;
   };

   const {inboundTransactions, outboundTransactions, synchronizations} = React.useMemo(() => {
      const inboundTransactions = filterByObjectType(
         results,
         DiplomatObjectType.enum.INBOUND_TRANSACTION
      );
      const outboundTransactions = filterByObjectType(
         results,
         DiplomatObjectType.enum.OUTBOUND_TRANSACTION
      );
      const synchronizations = filterByObjectType(results, DiplomatObjectType.enum.SYNCHRONIZATION);
      return {
         inboundTransactions,
         outboundTransactions,
         synchronizations,
         // transactionsFound: [...inboundTransactions, ...outboundTransactions] as IDiplomatObject[],
      };
   }, [results]);

   const transactionsFound = results;
   const isSyncFound = Boolean(synchronizations.length);
   const columnWidth = isSyncFound ? 4 : 6;

   return (
      <>
         <SearchMoveForm
            searchFieldsLabel={t('Include all transactions or find transactions with:')}
            searchResult={
               transactionsFound
                  ? t('{{itemsCount}} transactions found.', {itemsCount: transactionsFound.length})
                  : null
            }
            transaction
            onSubmit={handleSubmit}
         />
         <SearchResults
            label={t('Double-click to edit a transaction.')}
            data={transactionsFound}
            resultListComponent={
               <>
                  <Grid item xs={columnWidth}>
                     <Panel name={t('Inbound')}>
                        <SearchResultsList data={inboundTransactions} />
                     </Panel>
                  </Grid>
                  <Grid item xs={columnWidth}>
                     <Panel name={t('Outbound')}>
                        <SearchResultsList data={outboundTransactions} />
                     </Panel>
                  </Grid>
                  {isSyncFound && (
                     <Grid item xs={columnWidth}>
                        <Panel name={t('Synchronization')}>
                           <SearchResultsList data={synchronizations} />
                        </Panel>
                     </Grid>
                  )}
               </>
            }
            onExport={handleExport}
         />
         {onClose && (
            <Box textAlign={'center'}>
               <Button onClick={onClose}>{t('Close')}</Button>
            </Box>
         )}
      </>
   );
};

export default React.memo(SearchTransactionForm);
